import { PDFDocument } from 'pdf-lib';

import { AssetError } from './errors.ts';
import {
  ASSET_SCHEMA_VERSION,
  DEFAULT_ASSET_LIMITS,
  type AssetBinaryInput,
  type AssetLimits,
  type AssetPreflightResult,
  type AssetRecordV1,
  type PdfDocumentMetadata,
  type SupportedAssetMimeType,
} from './types.ts';

const MIME_BY_EXTENSION: Readonly<Record<string, SupportedAssetMimeType>> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
  svg: 'image/svg+xml',
  pdf: 'application/pdf',
};

const HEIC_EXTENSIONS = new Set(['heic', 'heif']);
const JPEG_SOF_MARKERS = new Set([
  0xc0, 0xc1, 0xc2, 0xc3, 0xc5, 0xc6, 0xc7, 0xc9, 0xca, 0xcb, 0xcd, 0xce, 0xcf,
]);

function readUint16(bytes: Uint8Array, offset: number, littleEndian: boolean): number {
  if (offset < 0 || offset + 2 > bytes.length) {
    throw new AssetError('ASSET_CORRUPT', 'El archivo termina antes de lo esperado.');
  }
  return littleEndian
    ? bytes[offset]! | (bytes[offset + 1]! << 8)
    : (bytes[offset]! << 8) | bytes[offset + 1]!;
}

function readUint32(bytes: Uint8Array, offset: number, littleEndian: boolean): number {
  if (offset < 0 || offset + 4 > bytes.length) {
    throw new AssetError('ASSET_CORRUPT', 'El archivo termina antes de lo esperado.');
  }
  const view = new DataView(bytes.buffer, bytes.byteOffset + offset, 4);
  return view.getUint32(0, littleEndian);
}

function extensionOf(fileName: string): string {
  const match = /\.([^.]+)$/.exec(fileName.trim());
  return match ? match[1]!.toLowerCase() : '';
}

function normalizedDeclaredMime(value: string | null | undefined): string | null {
  const mime = (value ?? '').split(';', 1)[0]!.trim().toLowerCase();
  if (!mime || mime === 'application/octet-stream') return null;
  return mime === 'image/jpg' ? 'image/jpeg' : mime;
}

function ascii(bytes: Uint8Array, start: number, length: number): string {
  return String.fromCharCode(...bytes.subarray(start, start + length));
}

function isHeic(bytes: Uint8Array, extension: string, declaredMime: string | null): boolean {
  if (HEIC_EXTENSIONS.has(extension) || declaredMime === 'image/heic' || declaredMime === 'image/heif') {
    return true;
  }
  if (bytes.length < 16 || ascii(bytes, 4, 4) !== 'ftyp') return false;
  const brands = ascii(bytes, 8, Math.min(bytes.length - 8, 32)).toLowerCase();
  return /heic|heix|hevc|hevx|heim|heis|mif1|msf1/.test(brands);
}

function sniffMime(bytes: Uint8Array): SupportedAssetMimeType | null {
  if (
    bytes.length >= 8 &&
    bytes[0] === 0x89 &&
    ascii(bytes, 1, 3) === 'PNG' &&
    bytes[4] === 0x0d &&
    bytes[5] === 0x0a &&
    bytes[6] === 0x1a &&
    bytes[7] === 0x0a
  ) return 'image/png';
  if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
    return 'image/jpeg';
  }
  if (bytes.length >= 12 && ascii(bytes, 0, 4) === 'RIFF' && ascii(bytes, 8, 4) === 'WEBP') {
    return 'image/webp';
  }
  if (bytes.length >= 5 && ascii(bytes, 0, 5) === '%PDF-') return 'application/pdf';

  const prefix = new TextDecoder('utf-8', { fatal: false })
    .decode(bytes.subarray(0, Math.min(bytes.length, 4096)))
    .replace(/^\uFEFF/, '')
    .trimStart();
  if (/^(?:<\?xml[\s\S]*?\?>\s*)?<svg\b/i.test(prefix)) return 'image/svg+xml';
  return null;
}

function validateMime(
  bytes: Uint8Array,
  fileName: string,
  declaredMimeType: string | null | undefined,
): SupportedAssetMimeType {
  const extension = extensionOf(fileName);
  const declared = normalizedDeclaredMime(declaredMimeType);
  if (isHeic(bytes, extension, declared)) {
    throw new AssetError(
      'ASSET_HEIC_UNSUPPORTED',
      'HEIC/HEIF no esta soportado sin un conversor real. Converti el archivo a JPG o PNG.',
      { fileName, declaredMimeType: declared },
    );
  }

  const sniffed = sniffMime(bytes);
  if (!sniffed) {
    throw new AssetError(
      'ASSET_FORMAT_UNSUPPORTED',
      'Formato no soportado. Usa PNG, JPG, WEBP, SVG o PDF.',
      { fileName, declaredMimeType: declared },
    );
  }

  const extensionMime = MIME_BY_EXTENSION[extension];
  if ((declared && declared !== sniffed) || (extensionMime && extensionMime !== sniffed)) {
    throw new AssetError(
      'ASSET_MIME_MISMATCH',
      'La extension o el tipo declarado no coincide con el contenido real del archivo.',
      { fileName, declaredMimeType: declared, detectedMimeType: sniffed },
    );
  }
  return sniffed;
}

function parsePngDimensions(bytes: Uint8Array): { widthPx: number; heightPx: number } {
  if (bytes.length < 24 || ascii(bytes, 12, 4) !== 'IHDR') {
    throw new AssetError('ASSET_CORRUPT', 'PNG invalido: falta el encabezado IHDR.');
  }
  return {
    widthPx: readUint32(bytes, 16, false),
    heightPx: readUint32(bytes, 20, false),
  };
}

function parseExifOrientation(bytes: Uint8Array, segmentStart: number, segmentLength: number): number | null {
  if (segmentLength < 14 || ascii(bytes, segmentStart, 6) !== 'Exif\0\0') return null;
  const tiff = segmentStart + 6;
  const order = ascii(bytes, tiff, 2);
  if (order !== 'II' && order !== 'MM') return null;
  const little = order === 'II';
  if (readUint16(bytes, tiff + 2, little) !== 42) return null;
  const ifdOffset = readUint32(bytes, tiff + 4, little);
  const ifd = tiff + ifdOffset;
  if (ifd + 2 > segmentStart + segmentLength) return null;
  const count = readUint16(bytes, ifd, little);
  for (let index = 0; index < count; index += 1) {
    const entry = ifd + 2 + index * 12;
    if (entry + 12 > segmentStart + segmentLength) return null;
    if (readUint16(bytes, entry, little) === 0x0112) {
      const orientation = readUint16(bytes, entry + 8, little);
      return orientation >= 1 && orientation <= 8 ? orientation : null;
    }
  }
  return null;
}

function parseJpeg(bytes: Uint8Array): {
  widthPx: number;
  heightPx: number;
  exifOrientation: number | null;
} {
  let offset = 2;
  let orientation: number | null = null;
  while (offset + 4 <= bytes.length) {
    while (offset < bytes.length && bytes[offset] === 0xff) offset += 1;
    if (offset >= bytes.length) break;
    const marker = bytes[offset]!;
    offset += 1;
    if (marker === 0xd9 || marker === 0xda) break;
    if (marker >= 0xd0 && marker <= 0xd7) continue;
    const length = readUint16(bytes, offset, false);
    if (length < 2 || offset + length > bytes.length) {
      throw new AssetError('ASSET_CORRUPT', 'JPEG invalido: segmento truncado.');
    }
    const dataStart = offset + 2;
    const dataLength = length - 2;
    if (marker === 0xe1) {
      orientation = parseExifOrientation(bytes, dataStart, dataLength) ?? orientation;
    }
    if (JPEG_SOF_MARKERS.has(marker)) {
      if (dataLength < 6) throw new AssetError('ASSET_CORRUPT', 'JPEG invalido: SOF truncado.');
      let heightPx = readUint16(bytes, dataStart + 1, false);
      let widthPx = readUint16(bytes, dataStart + 3, false);
      if (orientation && orientation >= 5) [widthPx, heightPx] = [heightPx, widthPx];
      return { widthPx, heightPx, exifOrientation: orientation };
    }
    offset += length;
  }
  throw new AssetError('ASSET_CORRUPT', 'JPEG invalido: no se encontraron dimensiones.');
}

function parseWebpDimensions(bytes: Uint8Array): { widthPx: number; heightPx: number } {
  const chunk = ascii(bytes, 12, 4);
  if (chunk === 'VP8X' && bytes.length >= 30) {
    return {
      widthPx: 1 + bytes[24]! + (bytes[25]! << 8) + (bytes[26]! << 16),
      heightPx: 1 + bytes[27]! + (bytes[28]! << 8) + (bytes[29]! << 16),
    };
  }
  if (chunk === 'VP8L' && bytes.length >= 25 && bytes[20] === 0x2f) {
    const b1 = bytes[21]!;
    const b2 = bytes[22]!;
    const b3 = bytes[23]!;
    const b4 = bytes[24]!;
    return {
      widthPx: 1 + ((b1 | (b2 << 8)) & 0x3fff),
      heightPx: 1 + (((b2 >> 6) | (b3 << 2) | (b4 << 10)) & 0x3fff),
    };
  }
  if (chunk === 'VP8 ' && bytes.length >= 30 && bytes[23] === 0x9d && bytes[24] === 0x01 && bytes[25] === 0x2a) {
    return {
      widthPx: readUint16(bytes, 26, true) & 0x3fff,
      heightPx: readUint16(bytes, 28, true) & 0x3fff,
    };
  }
  throw new AssetError('ASSET_CORRUPT', 'WEBP invalido o con encabezado no reconocido.');
}

function validateRasterLimits(
  dimensions: { widthPx: number; heightPx: number },
  limits: AssetLimits,
): void {
  const { widthPx, heightPx } = dimensions;
  if (!Number.isInteger(widthPx) || !Number.isInteger(heightPx) || widthPx <= 0 || heightPx <= 0) {
    throw new AssetError('ASSET_DIMENSIONS_INVALID', 'La imagen no declara dimensiones validas.');
  }
  const pixelCount = widthPx * heightPx;
  if (
    widthPx > limits.maxRasterDimensionPx ||
    heightPx > limits.maxRasterDimensionPx ||
    pixelCount > limits.maxRasterPixels
  ) {
    throw new AssetError(
      'ASSET_PIXEL_LIMIT',
      'La imagen supera el limite tecnico de dimensiones o pixeles.',
      { widthPx, heightPx, pixelCount, limits },
    );
  }
}

function parseSvgLength(value: string | undefined): number | null {
  if (!value) return null;
  const match = /^\s*(\d+(?:\.\d+)?)\s*(?:px)?\s*$/i.exec(value);
  return match ? Number(match[1]) : null;
}

function readAttribute(attributes: string, name: string): string | undefined {
  const expression = new RegExp('(?:^|\\s)' + name + '\\s*=\\s*(["\\\'])(.*?)\\1', 'i');
  return expression.exec(attributes)?.[2];
}

function parseSvg(bytes: Uint8Array, limits: AssetLimits): {
  widthPx: number;
  heightPx: number;
  viewBox: readonly [number, number, number, number] | null;
} {
  if (bytes.length > limits.maxSvgBytes) {
    throw new AssetError('ASSET_TOO_LARGE', 'El SVG supera el limite tecnico permitido.', {
      sizeBytes: bytes.length,
      maxSvgBytes: limits.maxSvgBytes,
    });
  }
  let source: string;
  try {
    source = new TextDecoder('utf-8', { fatal: true }).decode(bytes).replace(/^\uFEFF/, '');
  } catch (cause) {
    throw new AssetError('ASSET_CORRUPT', 'El SVG no es UTF-8 valido.', {}, { cause });
  }

  if (
    /<!DOCTYPE|<!ENTITY|<script\b|<foreignObject\b|@import|\son[a-z]+\s*=|javascript\s*:|data\s*:\s*text\/html/i.test(source) ||
    /(?:href|xlink:href)\s*=\s*(["'])(?!#)[\s\S]*?\1/i.test(source) ||
    /url\(\s*(["']?)(?!#)/i.test(source)
  ) {
    throw new AssetError(
      'ASSET_SVG_UNSAFE',
      'El SVG contiene scripts, eventos o recursos externos y fue rechazado.',
    );
  }
  const root = /<svg\b([^>]*)>/i.exec(source);
  if (!root) throw new AssetError('ASSET_CORRUPT', 'SVG invalido: falta el elemento raiz.');
  const attributes = root[1]!;
  const viewBoxText = readAttribute(attributes, 'viewBox');
  const viewBoxValues = viewBoxText
    ? viewBoxText.trim().split(/[\s,]+/).map(Number)
    : [];
  const viewBox =
    viewBoxValues.length === 4 && viewBoxValues.every(Number.isFinite) && viewBoxValues[2]! > 0 && viewBoxValues[3]! > 0
      ? (viewBoxValues as [number, number, number, number])
      : null;
  const widthPx = parseSvgLength(readAttribute(attributes, 'width')) ?? viewBox?.[2] ?? null;
  const heightPx = parseSvgLength(readAttribute(attributes, 'height')) ?? viewBox?.[3] ?? null;
  if (widthPx === null || heightPx === null) {
    throw new AssetError(
      'ASSET_DIMENSIONS_INVALID',
      'El SVG debe declarar width/height en px o un viewBox valido.',
    );
  }
  validateRasterLimits({ widthPx, heightPx }, limits);
  return { widthPx, heightPx, viewBox };
}

function dateIso(value: Date | undefined): string | null {
  return value && !Number.isNaN(value.getTime()) ? value.toISOString() : null;
}

async function parsePdf(bytes: Uint8Array, selectedPageIndex: number, limits: AssetLimits) {
  let document: PDFDocument;
  try {
    document = await PDFDocument.load(bytes, {
      ignoreEncryption: false,
      updateMetadata: false,
    });
  } catch (cause) {
    throw new AssetError(
      'ASSET_PDF_ENCRYPTED_OR_INVALID',
      'El PDF esta protegido, corrupto o usa una estructura no compatible.',
      {},
      { cause },
    );
  }
  const pages = document.getPages();
  if (pages.length > limits.maxPdfPages) {
    throw new AssetError(
      'ASSET_PDF_PAGE_LIMIT',
      'El PDF supera la cantidad maxima de paginas permitida.',
      { pageCount: pages.length, maxPdfPages: limits.maxPdfPages },
    );
  }
  if (!Number.isInteger(selectedPageIndex) || selectedPageIndex < 0 || selectedPageIndex >= pages.length) {
    throw new AssetError(
      'ASSET_PDF_PAGE_OUT_OF_RANGE',
      'La pagina seleccionada no existe en el PDF.',
      { selectedPageIndex, pageCount: pages.length },
    );
  }
  const metadata: PdfDocumentMetadata = {
    title: document.getTitle() ?? null,
    author: document.getAuthor() ?? null,
    subject: document.getSubject() ?? null,
    creator: document.getCreator() ?? null,
    producer: document.getProducer() ?? null,
    creationDateIso: dateIso(document.getCreationDate()),
    modificationDateIso: dateIso(document.getModificationDate()),
  };
  return {
    kind: 'pdf' as const,
    pageCount: pages.length,
    selectedPageIndex,
    pages: pages.map((page, pageIndex) => ({
      pageIndex,
      widthPt: page.getWidth(),
      heightPt: page.getHeight(),
      rotationDeg: page.getRotation().angle,
    })),
    document: metadata,
    originalBytesPreserved: true as const,
  };
}

export async function binaryInputToBytes(input: AssetBinaryInput): Promise<Uint8Array> {
  if (input instanceof Uint8Array) return input.slice();
  if (input instanceof ArrayBuffer) return new Uint8Array(input.slice(0));
  if (typeof Blob !== 'undefined' && input instanceof Blob) {
    return new Uint8Array(await input.arrayBuffer());
  }
  throw new AssetError('ASSET_FORMAT_UNSUPPORTED', 'Entrada binaria no soportada.');
}

export async function sha256Hex(bytes: Uint8Array): Promise<string> {
  if (!globalThis.crypto?.subtle) {
    throw new AssetError('ASSET_FORMAT_UNSUPPORTED', 'SHA-256 no esta disponible en este entorno.');
  }
  const stable = Uint8Array.from(bytes);
  const digest = await globalThis.crypto.subtle.digest('SHA-256', stable.buffer);
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
}

export async function preflightAsset(
  input: {
    readonly bytes: AssetBinaryInput;
    readonly fileName: string;
    readonly declaredMimeType?: string | null;
    readonly selectedPdfPageIndex?: number;
    readonly now?: string;
  },
  limits: AssetLimits = DEFAULT_ASSET_LIMITS,
): Promise<AssetPreflightResult> {
  const bytes = await binaryInputToBytes(input.bytes);
  if (bytes.length === 0) throw new AssetError('ASSET_EMPTY', 'El archivo esta vacio.');
  if (bytes.length > limits.maxFileBytes) {
    throw new AssetError('ASSET_TOO_LARGE', 'El archivo supera el limite tecnico permitido.', {
      sizeBytes: bytes.length,
      maxFileBytes: limits.maxFileBytes,
    });
  }
  const mimeType = validateMime(bytes, input.fileName, input.declaredMimeType);
  const hash = await sha256Hex(bytes);
  const now = input.now ?? new Date().toISOString();
  let metadata: AssetRecordV1['metadata'];
  if (mimeType === 'application/pdf') {
    metadata = await parsePdf(bytes, input.selectedPdfPageIndex ?? 0, limits);
  } else if (mimeType === 'image/svg+xml') {
    metadata = { kind: 'svg', ...parseSvg(bytes, limits), activeContentChecksPassed: true };
  } else {
    const raster = mimeType === 'image/png'
      ? { ...parsePngDimensions(bytes), exifOrientation: null }
      : mimeType === 'image/jpeg'
        ? parseJpeg(bytes)
        : { ...parseWebpDimensions(bytes), exifOrientation: null };
    validateRasterLimits(raster, limits);
    metadata = {
      kind: 'raster-image',
      ...raster,
      pixelCount: raster.widthPx * raster.heightPx,
    };
  }
  const pageSuffix = metadata.kind === 'pdf' ? '-p' + String(metadata.selectedPageIndex + 1) : '';
  const assetId = 'asset-' + hash.slice(0, 24) + pageSuffix;
  return {
    bytes,
    record: {
      schemaVersion: ASSET_SCHEMA_VERSION,
      assetId,
      contentHash: hash,
      originalFileName: input.fileName,
      displayName: input.fileName,
      mimeType,
      sizeBytes: bytes.length,
      createdAt: now,
      updatedAt: now,
      metadata,
    },
  };
}

export function serializeAssetRecord(record: AssetRecordV1): string {
  return JSON.stringify(record);
}

export function deserializeAssetRecord(serialized: string): AssetRecordV1 {
  const parsed = JSON.parse(serialized) as Partial<AssetRecordV1>;
  if (
    parsed.schemaVersion !== ASSET_SCHEMA_VERSION ||
    typeof parsed.assetId !== 'string' ||
    typeof parsed.contentHash !== 'string' ||
    typeof parsed.mimeType !== 'string' ||
    typeof parsed.sizeBytes !== 'number' ||
    typeof parsed.metadata !== 'object' ||
    parsed.metadata === null
  ) {
    throw new AssetError('ASSET_CORRUPT', 'Descriptor de activo invalido o incompatible.');
  }
  return parsed as AssetRecordV1;
}
